// Types mirror the JSON from src/web/dashboard.ts.

export type PlanId = 'free' | 'premium';
export type FollowMode = 'auto' | 'allowlist' | 'manual';

export interface User {
  id: string;
  username: string;
  avatarUrl: string | null;
}

export interface GuildSummary {
  id: string;
  name: string;
  iconUrl: string | null;
  botPresent: boolean;
  plan: PlanId | null;
  inviteUrl: string | null;
}

export interface Me {
  user: User;
  optedOut: boolean;
  billingEnabled: boolean;
  inviteUrl: string | null;
  guilds: GuildSummary[];
}

export interface PlanLimits {
  id: PlanId;
  name: string;
  maxListeners: number;
  maxBufferSeconds: number;
  maxRecordMinutes: number;
  maxRetentionDays: number;
  perUserClips: boolean;
  perSpeakerDownloads: boolean;
  autoRecord: boolean;
  cameras: boolean;
  transcripts: boolean;
  maxKeptClips: number;
  playClips: boolean;
  weeklyRecap: boolean;
}

export interface Settings {
  bufferSeconds: number;
  announceChannelId: string | null;
  retentionDays: number;
  maxRecordMinutes: number;
  followMode: FollowMode;
  minHumans: number;
  cameraEnabled: boolean;
  transcripts: boolean;
  talkStats: boolean;
  voiceClips: boolean;
  clipSound: boolean;
  clipChannelId: string | null;
  transcriptLanguage: string | null;
  recapChannelId: string | null;
}

export interface ChannelInfo {
  id: string;
  name: string;
  category: string | null;
}

export interface RoleInfo {
  id: string;
  name: string;
  color: string | null;
}

export interface LiveListener {
  botId: string;
  botName: string;
  channelId: string;
  paused: boolean;
  startedAt: number;
  speakers: number;
  cameras: number;
  recording: { id: string; elapsedMs: number; speakers: number } | null;
}

export interface Subscription {
  status: string;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  purchasedBy: string;
  purchasedByName: string | null;
  isPurchaser: boolean;
}

export interface GuildData {
  guild: { id: string; name: string; iconUrl: string | null; voiceChannels: ChannelInfo[]; textChannels: ChannelInfo[]; roles: RoleInfo[] };
  settings: Settings;
  effective: Settings;
  rules: { block: string[]; allow: string[]; autoRecord: string[] };
  excludedRoles: string[];
  live: LiveListener[];
  cameraCaptureAvailable: boolean;
  speech: { available: boolean; summaries: boolean; englishOnly: boolean };
  keptClips: number;
  clipSoundAvailable: boolean;
  listeners: number;
  plan: PlanLimits & {
    comped: boolean;
    billingEnabled: boolean;
    prices: { month: boolean; year: boolean };
    subscription: Subscription | null;
    graceEndsAt: number | null;
  };
  plans: { free: PlanLimits; premium: PlanLimits };
}

export interface RecordingItem {
  id: string;
  channelId: string;
  channelName: string | null;
  startedAt: number;
  durationMs: number | null;
  status: 'recording' | 'processing' | 'ready' | 'failed';
  error: string | null;
  auto: boolean;
  startedBy: string;
  startedByName: string | null;
  speakers: string[];
  url: string | null;
}

/** Someone who was in the call when a clip was made. */
export interface Person {
  userId: string;
  name: string;
  avatarUrl: string | null;
  /** Heard in the clip. */
  spoke: boolean;
}

export interface ClipItem {
  id: string;
  title: string | null;
  description: string | null;
  createdAt: number;
  seconds: number;
  channelName: string | null;
  createdBy: string;
  createdByName: string | null;
  participants: Person[];
  /** ⭐ Kept forever (spared from the retention cleanup). */
  kept: boolean;
  /** Download link. */
  url: string;
  /** Streams it for the player. */
  audioUrl: string;
  /** The clip's own page, to share. */
  pageUrl: string;
}

export interface Page<T> {
  items: T[];
  next: number | null;
}

export interface AuditItem {
  at: number;
  userId: string;
  userName: string | null;
  action: string;
  detail: unknown;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: Record<string, unknown>,
  ) {
    super(message);
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  // Every non-GET call sends a JSON body: Fastify rejects an empty one with a JSON content type.
  const send = method !== 'GET' && method !== 'DELETE';
  const res = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: send ? { 'content-type': 'application/json' } : {},
    body: send ? JSON.stringify(body ?? {}) : undefined,
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throw new ApiError(typeof data.error === 'string' ? data.error : `Request failed (${res.status})`, res.status, data);
  return data as T;
}

const g = (id: string) => `/api/guilds/${encodeURIComponent(id)}`;

export const api = {
  me: () => request<Me>('GET', '/api/me'),
  logout: () => request<{ ok: true }>('POST', '/logout'),
  setOptOut: (optedOut: boolean) => request<{ optedOut: boolean }>('PUT', '/api/me/privacy', { optedOut }),
  guild: (id: string) => request<GuildData>('GET', g(id)),
  saveSettings: (id: string, patch: Partial<Settings>) => request<GuildData>('PATCH', `${g(id)}/settings`, patch),
  setChannelRule: (id: string, channelId: string, rule: { block?: boolean; allow?: boolean; autoRecord?: boolean }) =>
    request<GuildData>('PUT', `${g(id)}/channels/${channelId}`, rule),
  setExcludedRoles: (id: string, roleIds: string[]) => request<GuildData>('PUT', `${g(id)}/excluded-roles`, { roleIds }),
  recordings: (id: string, before?: number) => request<Page<RecordingItem> & { retentionDays: number }>('GET', `${g(id)}/recordings${before ? `?before=${before}` : ''}`),
  deleteRecording: (id: string, recId: string) => request<{ ok: true }>('DELETE', `${g(id)}/recordings/${recId}`),
  clips: (id: string, before?: number) => request<Page<ClipItem>>('GET', `${g(id)}/clips${before ? `?before=${before}` : ''}`),
  updateClip: (id: string, clipId: string, details: { title?: string | null; description?: string | null; kept?: boolean }) =>
    request<ClipItem>('PATCH', `${g(id)}/clips/${clipId}`, details),
  deleteClip: (id: string, clipId: string) => request<{ ok: true }>('DELETE', `${g(id)}/clips/${clipId}`),
  leave: (id: string, botId: string) => request<GuildData>('POST', `${g(id)}/live/${botId}/leave`),
  stopRecording: (id: string, botId: string) => request<GuildData>('POST', `${g(id)}/live/${botId}/stop-recording`),
  audit: (id: string) => request<{ items: AuditItem[] }>('GET', `${g(id)}/audit`),
  checkout: (id: string, interval: 'month' | 'year') => request<{ url: string }>('POST', `${g(id)}/billing/checkout`, { interval }),
  portal: (id: string) => request<{ url: string }>('POST', `${g(id)}/billing/portal`),
};
